import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import SEO from '../components/SEO';

const zoomIn = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1.1);
  }
`;

const GalleryContainer = styled.div`
  padding-top: 0;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, rgba(26, 77, 128, 0.8) 0%, rgba(44, 62, 80, 0.7) 100%), url('/gallery_heropage.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  color: ${props => props.theme.colors.white};
  padding: 120px 0 80px;
  text-align: center;
  position: relative;
  min-height: 60vh;
  display: flex;
  align-items: center;
  animation: ${zoomIn} 10s ease-in-out infinite;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    background-attachment: scroll;
    min-height: 50vh;
    padding: 100px 0 60px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Section = styled.section`
  padding: 80px 0;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 60px 0;
  }
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  margin-bottom: 60px;
  color: ${props => props.theme.colors.primary};
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background: ${props => props.theme.colors.secondary};
    margin: 20px auto 0;
    border-radius: 2px;
  }
`;

const FilterButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 40px;
  flex-wrap: wrap;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: 10px;
  }
`;

const FilterButton = styled.button`
  padding: 10px 20px;
  border: 2px solid ${props => props.active ? props.theme.colors.secondary : props.theme.colors.primary};
  background: ${props => props.active ? props.theme.colors.secondary : 'transparent'};
  color: ${props => props.active ? props.theme.colors.white : props.theme.colors.primary};
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  
  &:hover {
    background: ${props => props.theme.colors.secondary};
    color: ${props => props.theme.colors.white};
    border-color: ${props => props.theme.colors.secondary};
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
`;

const GalleryItem = styled(motion.div)`
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
    
    .overlay {
      opacity: 1;
    }
  }
  
  img {
    width: 100%;
    height: 250px;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.1);
  }
  
  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(26, 77, 128, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    
    h3 {
      color: white;
      text-align: center;
      font-size: 1.2rem;
    }
  }
`;

const Lightbox = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LightboxContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
  
  .close-btn {
    position: absolute;
    top: -50px;
    right: 0;
    background: none;
    border: none;
    color: white;
    font-size: 2rem;
    cursor: pointer;
    padding: 10px;
  }
  
  .nav-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 15px;
    border-radius: 50%;
    transition: background 0.3s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
    
    &.prev {
      left: -80px;
    }
    
    &.next {
      right: -80px;
    }
  }
`;

// Recent Work Section Styles
const RecentWorkSection = styled.section`
  padding: 80px 0;
  background: #f8f9fa;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 60px 0;
  }
`;

const RecentWorkTitle = styled(motion.h2)`
  text-align: center;
  text-transform: uppercase;
  margin-bottom: 60px;
  color: ${props => props.theme.colors.primary};
  font-size: 2.5rem;
  font-weight: 700;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background: #f39c12;
    margin: 20px auto 0;
    border-radius: 0;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  padding-top: 11px;
  margin-bottom: 40px;
`;

const CarouselInner = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
`;

const CarouselItem = styled.div`
  display: ${props => props.active ? 'block' : 'none'};
`;

const CarouselRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
`;

const Thumbnail = styled.div`
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
  
  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const CarouselControl = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(26, 77, 128, 0.8);
  border: none;
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
  
  &:hover {
    background: rgba(26, 77, 128, 1);
    transform: translateY(-50%) scale(1.1);
  }
  
  &.prev {
    left: -25px;
  }
  
  &.next {
    right: -25px;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 40px;
    height: 40px;
    
    &.prev {
      left: -20px;
    }
    
    &.next {
      right: -20px;
    }
  }
`;

const DescriptionText = styled.span`
  display: block;
  color: ${props => props.theme.colors.gray};
  line-height: 1.8;
  margin: 30px 0;
  font-size: 1.1rem;
  text-align: center;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

const ServicesSection = styled.div`
  margin: 40px 0;
`;

const ServicesTitle = styled.p`
  font-size: 20px;
  color: maroon;
  text-align: center;
  margin-bottom: 30px;
  font-weight: 600;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 18px;
  }
`;

const ServicesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  
  td {
    padding: 15px;
    border: 1px solid #dee2e6;
    text-align: center;
    font-size: 0.9rem;
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      padding: 10px;
      font-size: 0.8rem;
    }
  }
  
  tr:nth-child(even) {
    background: #f8f9fa;
  }
  
  tr:hover {
    background: #e9ecef;
  }
`;

// Gallery Section Styles
const GallerySection = styled.section`
  padding: 80px 0;
  background: #f9f8f8;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 60px 0;
  }
`;

const GalleryTitle = styled(motion.h3)`
  text-align: center;
  text-transform: uppercase;
  margin-bottom: 60px;
  color: ${props => props.theme.colors.primary};
  font-size: 2.5rem;
  font-weight: 700;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background: #f39c12;
    margin: 20px auto 0;
    border-radius: 0;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const FilterContainer = styled.ul`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 40px;
  flex-wrap: wrap;
  list-style: none;
  padding: 0;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: 10px;
  }
`;

const FilterItem = styled.li`
  &.port-filter.all.active a,
  &.cat-item-1.active a,
  &.cat-item-2.active a,
  &.cat-item-3.active a,
  &.cat-item-4.active a,
  &.cat-item-5.active a {
    background: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.white};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const FilterLink = styled.a`
  padding: 10px 20px;
  border: 2px solid ${props => props.theme.colors.primary};
  background: transparent;
  color: ${props => props.theme.colors.primary};
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  text-decoration: none;
  display: inline-block;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.white};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const PortfolioArea = styled.ul`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  list-style: none;
  padding: 0;
  margin: 0;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }
`;

const PortfolioItem = styled(motion.li)`
  position: relative;
  border-radius: 0;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
    
    .port-info {
      opacity: 1;
    }
  }
  
  img {
    width: 100%;
    height: 180px;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.1);
  }
`;

const ImageBlock = styled.span`
  position: relative;
  display: block;
  
  .port-info {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(26, 77, 128, 0.8);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    
    h5 {
      color: white;
      text-align: center;
      font-size: 1.2rem;
      margin: 0 0 10px 0;
    }
    
    p {
      color: white;
      text-align: center;
      font-size: 1rem;
      margin: 0;
      font-family: 'Times New Roman', Times, serif;
    }
  }
`;

const ImageZoom = styled.a`
  display: block;
  text-decoration: none;
`;

const Gallery = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentCarouselSlide, setCurrentCarouselSlide] = useState(0);
  const [activeFilter, setActiveFilter] = useState('All');

  const recentWorkData = [
    [
      { image: '/IMG-20250802-WA0055.jpg', alt: 'German hangers super structures tents for rent for hire in bangalore trimurthy tent house' },
      { image: '/IMG-20250802-WA0056.jpg', alt: 'Wooden stages and led walls for rent in bangalore trimurthy tent house' },
      { image: '/IMG-20250802-WA0057.jpg', alt: 'Aluminium tents for rent for hire in bangalore trimurthy tent house' },
      { image: '/IMG-20250802-WA0058.jpg', alt: 'Golden queue stands with red ribbons for rent for hire in bangalore trimurthy tent house' }
    ],
    [
      { image: '/IMG-20250802-WA0059.jpg', alt: 'Exhibition stalls for rent in bangalore for events and exhibitions trimurthy tent house' },
      { image: '/IMG-20250802-WA0060.jpg', alt: 'German tents and synthetic carpets for rent in bangalore trimurthy tent house' },
      { image: '/IMG-20250802-WA0061.jpg', alt: 'Octanorm exhibition stalls for rent for hire for events tradeshows exhibitions in bangalore trimurthy tent house' },
      { image: '/IMG-20250802-WA0062.jpg', alt: 'Tents canopies shamianas tables chairs carpets for rent for weddings in bangalore trimurthy tent house' }
    ],
    [
      { image: '/IMG-20250802-WA0063.jpg', alt: 'Professional tent setup for corporate events in bangalore trimurthy tent house' },
      { image: '/IMG-20250802-WA0064.jpg', alt: 'Wedding reception tent arrangements in bangalore trimurthy tent house' },
      { image: '/IMG-20250802-WA0065.jpg', alt: 'Exhibition and trade show setups in bangalore trimurthy tent house' },
      { image: '/IMG-20250802-WA0066.jpg', alt: 'Conference and seminar tent arrangements in bangalore trimurthy tent house' }
    ],
    [
      { image: '/IMG-20250802-WA0067.jpg', alt: 'Outdoor event tent installations in bangalore trimurthy tent house' },
      { image: '/IMG-20250802-WA0068.jpg', alt: 'Birthday party tent decorations in bangalore trimurthy tent house' },
      { image: '/IMG-20250802-WA0069.jpg', alt: 'Product launch event setups in bangalore trimurthy tent house' },
      { image: '/IMG-20250802-WA0070.jpg', alt: 'Cultural event tent arrangements in bangalore trimurthy tent house' }
    ],
    [
      { image: '/IMG-20250802-WA0071.jpg', alt: 'Corporate meeting tent setups in bangalore trimurthy tent house' },
      { image: '/IMG-20250802-WA0072.jpg', alt: 'Award ceremony tent arrangements in bangalore trimurthy tent house' },
      { image: '/IMG-20250802-WA0073.jpg', alt: 'Indoor event tent installations in bangalore trimurthy tent house' },
      { image: '/IMG-20250802-WA0074.jpg', alt: 'Fashion show tent setups in bangalore trimurthy tent house' }
    ],
    [
      { image: '/IMG-20250802-WA0075.jpg', alt: 'Music concert tent arrangements in bangalore trimurthy tent house' },
      { image: '/IMG-20250802-WA0076.jpg', alt: 'Food festival tent setups in bangalore trimurthy tent house' },
      { image: '/IMG-20250802-WA0077.jpg', alt: 'Art exhibition tent installations in bangalore trimurthy tent house' },
      { image: '/IMG-20250802-WA0078.jpg', alt: 'Sports event tent arrangements in bangalore trimurthy tent house' }
    ],
    [
      { image: '/IMG-20250802-WA0021.jpg', alt: 'German hanger tent structure for large events and exhibitions trimurthy tent house bangalore' },
      { image: '/IMG-20250802-WA0024.jpg', alt: 'Interior view of german hanger tent with professional setup trimurthy tent house' },
      { image: '/IMG-20250802-WA0025.jpg', alt: 'German hanger tent ready for events with complete infrastructure trimurthy tent house' },
      { image: '/IMG-20250802-WA0027.jpg', alt: 'German hanger tent with advanced lighting and sound systems trimurthy tent house' }
    ]
  ];

  const galleryData = [
    // Warehousing
    { id: 1, category: 'Warehousing', image: '/IMG-20250802-WA0079.jpg', alt: 'Extensive warehousing facilities trimurthy tent house services provider supplier dealer in bangalore' },
    { id: 2, category: 'Warehousing', image: '/IMG-20250802-WA0080.jpg', alt: 'Quality control and maintenance of vast warehouses trimurthy tent house supplier dealer in bangalore' },
    { id: 3, category: 'Warehousing', image: '/IMG-20250802-WA0081.jpg', alt: 'Warehousing facility with quality control trimurthy tent house bangalore karnataka tenthouse supplier' },
    { id: 4, category: 'Warehousing', image: '/IMG-20250802-WA0082.jpg', alt: 'Warehouse trimurthy tent house bangalore tent house supplier and dealer in bangalore' },
    { id: 5, category: 'Warehousing', image: '/IMG-20250802-WA0083.jpg', alt: 'Warehousing facility trimurthy tent house bangalore tent house dealer supplier' },
    
    // Exhibitions
    { id: 6, category: 'Exhibitions', image: '/IMG-20250802-WA0084.jpg', alt: 'Exhibition stalls for rent in bangalore for events and exhibitions trimurthy tent house' },
    { id: 7, category: 'Exhibitions', image: '/IMG-20250802-WA0085.jpg', alt: 'Octanorm exhibition stalls for rent for hire in bangalore for events and exhibitions trimurthy tent house' },
    { id: 8, category: 'Exhibitions', image: '/IMG-20250802-WA0086.jpg', alt: 'German tents and synthetic carpets for rent in bangalore trimurthy tent house' },
    { id: 9, category: 'Exhibitions', image: '/IMG-20250802-WA0087.jpg', alt: 'Exhibition stalls for rent for hire in bangalore trimurthy tent house' },
    { id: 10, category: 'Exhibitions', image: '/IMG-20250802-WA0088.jpg', alt: 'Exhibitions stalls on rent for events and exhibitions bangalore trimurthy tent house' },
    { id: 11, category: 'Exhibitions', image: '/IMG-20250802-WA0089.jpg', alt: 'Exhibition stalls for rent for hire for events and exhibitions in bangalore trimurthy tent house' },
    { id: 12, category: 'Exhibitions', image: '/IMG-20250802-WA0090.jpg', alt: 'Exhibition stalls for rent in bangalore from trimurthy hi tech tent house' },
    { id: 13, category: 'Exhibitions', image: '/IMG-20250802-WA0091.jpg', alt: 'Octanorm exhibition stalls for rent for hire for events tradeshows exhibitions in bangalore trimurthy tent house' },
    { id: 14, category: 'Exhibitions', image: '/IMG-20250802-WA0092.jpg', alt: 'Exhibition stalls for rent for hire in bangalore contact trimurthy tent house bangalore karnataka' },
    { id: 15, category: 'Exhibitions', image: '/IMG-20250802-WA0093.jpg', alt: 'Exhibition stalls and synthetic red carpets for rent in bangalore trimurthy tent house' },
    
    // Events
    { id: 16, category: 'Events', image: '/IMG-20250802-WA0055.jpg', alt: 'Super structures mega hangers tents for rent in bangalore trimurthy tent house' },
    { id: 17, category: 'Events', image: '/IMG-20250802-WA0056.jpg', alt: 'Round tables and chairs for rent for hire in bangalore trimurthy tent house' },
    { id: 18, category: 'Events', image: '/IMG-20250802-WA0057.jpg', alt: 'Waterproof sheds industrial sheds for rent for hire in bangalore trimurthy tent house' },
    { id: 19, category: 'Events', image: '/IMG-20250802-WA0058.jpg', alt: 'Led walls and led lighting for rent for hire for events exhibitions bangalore trimurthy tent house' },
    { id: 20, category: 'Events', image: '/IMG-20250802-WA0059.jpg', alt: 'Aluminium tents for rent for hire in bangalore trimurthy tent house' },
    { id: 21, category: 'Events', image: '/IMG-20250802-WA0060.jpg', alt: 'Golden queue stands with red ribbons for rent for hire in bangalore trimurthy tent house' },
    { id: 22, category: 'Events', image: '/IMG-20250802-WA0061.jpg', alt: 'Ac german hanger tent for rent for hire in bangalore trimurthy tent house' },
    { id: 23, category: 'Events', image: '/IMG-20250802-WA0062.jpg', alt: 'German tents for rent for hire in bangalore trimurthy tent house' },
    { id: 24, category: 'Events', image: '/IMG-20250802-WA0063.jpg', alt: 'German tents aluminium tents for rent in bangalore trimurthy tent house' },
    { id: 25, category: 'Events', image: '/IMG-20250802-WA0064.jpg', alt: 'German hangers super structures tents for rent for hire in bangalore trimurthy tent house' },
    
    // Weddings
    { id: 26, category: 'Weddings', image: '/IMG-20250802-WA0065.jpg', alt: 'Tables and chairs for rent for weddings in bangalore trimurthy tent house' },
    { id: 27, category: 'Weddings', image: '/IMG-20250802-WA0066.jpg', alt: 'Banquet tables chairs tents for rent for weddings in bangalore trimurthy tent house' },
    { id: 28, category: 'Weddings', image: '/IMG-20250802-WA0067.jpg', alt: 'Round tables and chairs with cloth covering for rent for wedding functions in bangalore trimurthy tent house' },
    { id: 29, category: 'Weddings', image: '/IMG-20250802-WA0068.jpg', alt: 'Tent houses for rent for weddings and wedding functions in bangalore trimurthy tent house' },
    { id: 30, category: 'Weddings', image: '/IMG-20250802-WA0069.jpg', alt: 'Tents tables chairs carpets fans air coolers acs for rent for weddings in bangalore trimurthy tent house' },
    { id: 31, category: 'Weddings', image: '/IMG-20250802-WA0070.jpg', alt: 'Tent houses for rent for weddings and wedding functions from trimurthy tent house in bangalore' },
    { id: 32, category: 'Weddings', image: '/IMG-20250802-WA0071.jpg', alt: 'Tents canopies shamianas tables chairs carpets for rent for weddings in bangalore trimurthy tent house' },
    { id: 33, category: 'Weddings', image: '/IMG-20250802-WA0072.jpg', alt: 'Tent houses supplier dealer for weddings and wedding functions in bangalore trimurthy tent house' },
    { id: 34, category: 'Weddings', image: '/IMG-20250802-WA0073.jpg', alt: 'Shamiyanas canopies tables chairs wedding chairs wooden stage for rent in bangalore trimurthy tent house' },
    { id: 35, category: 'Weddings', image: '/IMG-20250802-WA0074.jpg', alt: 'Banquet tables and chairs for rent for weddings in bangalore trimurthy tent house' },
    
    // German Hangers
    { id: 36, category: 'German Hangers', image: '/IMG-20250802-WA0021.jpg', alt: 'German hanger tent structure for large events and exhibitions trimurthy tent house bangalore' },
    { id: 37, category: 'German Hangers', image: '/IMG-20250802-WA0024.jpg', alt: 'Interior view of german hanger tent with professional setup trimurthy tent house' },
    { id: 38, category: 'German Hangers', image: '/IMG-20250802-WA0025.jpg', alt: 'German hanger tent ready for events with complete infrastructure trimurthy tent house' },
    { id: 39, category: 'German Hangers', image: '/IMG-20250802-WA0027.jpg', alt: 'German hanger tent with advanced lighting and sound systems trimurthy tent house' },
    { id: 40, category: 'German Hangers', image: '/IMG-20250802-WA0028.jpg', alt: 'Custom german hanger tent setup for special events trimurthy tent house' },
    { id: 41, category: 'German Hangers', image: '/IMG-20250802-WA0030.jpg', alt: 'German hanger tent perfect for large scale exhibitions trimurthy tent house' },
    { id: 42, category: 'German Hangers', image: '/IMG-20250802-WA0031.jpg', alt: 'German hanger tent with concert and live performance setup trimurthy tent house' },
    { id: 43, category: 'German Hangers', image: '/IMG-20250802-WA0032.jpg', alt: 'German hanger tent for sports events and activities trimurthy tent house' },
    { id: 44, category: 'German Hangers', image: '/IMG-20250802-WA0033.jpg', alt: 'German hanger tent for festivals and cultural events trimurthy tent house' },
    { id: 45, category: 'German Hangers', image: '/IMG-20250802-WA0034.jpg', alt: 'German hanger tent for trade fairs and business events trimurthy tent house' },
    { id: 46, category: 'German Hangers', image: '/IMG-20250802-WA0044.jpg', alt: 'German hanger tent with comfortable seating arrangements trimurthy tent house' },
    { id: 47, category: 'German Hangers', image: '/IMG-20250802-WA0045.jpg', alt: 'German hanger tent with integrated stage for performances trimurthy tent house' },
    { id: 48, category: 'German Hangers', image: '/IMG-20250802-WA0047.jpg', alt: 'German hanger tent with professional event setup trimurthy tent house' },
    { id: 49, category: 'German Hangers', image: '/IMG-20250802-WA0060.jpg', alt: 'German hanger tent for special celebrations and events trimurthy tent house' },
    { id: 50, category: 'German Hangers', image: '/IMG-20250802-WA0065.jpg', alt: 'German hanger tent with premium event infrastructure trimurthy tent house' },
    { id: 51, category: 'German Hangers', image: '/IMG-20250802-WA0070.jpg', alt: 'German hanger tent for grand celebrations and functions trimurthy tent house' },
    { id: 52, category: 'German Hangers', image: '/IMG-20250802-WA0037.jpg', alt: 'German hanger tent with beautiful interior decoration trimurthy tent house' },
    { id: 53, category: 'German Hangers', image: '/gallery_heropage.jpg', alt: 'Gallery showcase of german hanger tents and structures trimurthy tent house' },
    { id: 54, category: 'German Hangers', image: '/IMG-20250802-WA0041.jpg', alt: 'Exterior view of german hanger tent structure trimurthy tent house' },
    { id: 55, category: 'German Hangers', image: '/IMG-20250802-WA0042.jpg', alt: 'German hanger tent illuminated at night for events trimurthy tent house' },
    { id: 56, category: 'German Hangers', image: '/IMG-20250802-WA0043.jpg', alt: 'German hanger tent setup process and construction trimurthy tent house' }
  ];

  const filteredGallery = activeFilter === 'All' 
    ? galleryData 
    : galleryData.filter(item => item.category === activeFilter);

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === filteredGallery.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? filteredGallery.length - 1 : prev - 1
    );
  };

  const nextCarouselSlide = () => {
    setCurrentCarouselSlide((prev) => 
      prev === recentWorkData.length - 1 ? 0 : prev + 1
    );
  };

  const prevCarouselSlide = () => {
    setCurrentCarouselSlide((prev) => 
      prev === 0 ? recentWorkData.length - 1 : prev - 1
    );
  };

  return (
    <GalleryContainer>
      <SEO
        title="Get Tent Rentals & Event Solutions in Bangalore | Trimurthy Tent House Services"
        description="Book premium tent house services for weddings, exhibitions and events in Bangalore. Get German hangers, tents and stages. Call 88808 28698."
        canonical="https://ttheventorganisers.in/gallery"
      />
      <HeroSection>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 style={{ color: '#ffffff' }}>Our Gallery</h1>
            <p style={{ 
              fontSize: '1.5rem', 
              opacity: 0.9, 
              maxWidth: '600px', 
              margin: '20px auto 0',
              color: '#ffffff',
              fontWeight: 600,
              textShadow: '1px 1px 6px rgba(0,0,0,0.5)',
              fontFamily: "'Times New Roman', Times, serif"
            }}>
              Showcasing Our Legacy of Successful Events & Spectacular Tent Setups
            </p>
          </motion.div>
        </Container>
      </HeroSection>

      <RecentWorkSection>
        <Container>
          <RecentWorkTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Our Recent Work
          </RecentWorkTitle>

          <CarouselContainer>
            <CarouselInner>
              {recentWorkData.map((slide, slideIndex) => (
                <CarouselItem key={slideIndex} active={slideIndex === currentCarouselSlide}>
                  <CarouselRow>
                    {slide.map((item, itemIndex) => (
                      <Thumbnail key={itemIndex}>
                        <img src={item.image} alt={item.alt} title={item.alt} />
                      </Thumbnail>
                    ))}
                  </CarouselRow>
                </CarouselItem>
              ))}
            </CarouselInner>
            
            <CarouselControl className="prev" onClick={prevCarouselSlide}>
              <FiChevronLeft />
            </CarouselControl>
            <CarouselControl className="next" onClick={nextCarouselSlide}>
              <FiChevronRight />
            </CarouselControl>
          </CarouselContainer>

          <DescriptionText>
            Trimurthy Tent House is a 36-year old trusted Tent House services providers, tent house services dealer and supplier in Bangalore.
          </DescriptionText>

          <ServicesSection>
            <ServicesTitle>
              We provide tent house services for the following products in and around Bangalore and across Karnataka.
            </ServicesTitle>
            
            <ServicesTable>
              <tbody>
                <tr>
                  <td>Tent House for rent in Bangalore, Karnataka</td>
                  <td>Tent House Dealer in Bangalore, Karnataka</td>
                  <td>Transparent German Hangers for rent / for hire</td>
                </tr>
                <tr>
                  <td>Chairs for rent in Bangalore, Karnataka</td>
                  <td>Canopies for rent in Bangalore, Karnataka</td>
                  <td>Waterproof Plywood Platforms for rent / for hire</td>
                </tr>
                <tr>
                  <td>Tent House Supplier in Bangalore, Karnataka</td>
                  <td>Aluminium Tents for rent in Bangalore, Karnataka</td>
                  <td>Air Conditioners of various tonnes for rent / for hire</td>
                </tr>
                <tr>
                  <td>Shamianas for rent in Bangalore, Karnataka</td>
                  <td>Shamiyana Tent for rent in Bangalore</td>
                  <td>Golden Queue Stands with Red Ribbins for rent / for hire</td>
                </tr>
                <tr>
                  <td>German Tents for rent</td>
                  <td>Aluminium Hanger Tents for rent</td>
                  <td>Single Seater Leather Sofas for rent / for hire</td>
                </tr>
                <tr>
                  <td>Pagoda Tents for rent</td>
                  <td>Exhibition Stalls for rent</td>
                  <td>Plastic Chairs with covers for rent / for hire</td>
                </tr>
                <tr>
                  <td>Octanorm Exhibition Stalls for rent</td>
                  <td>Super Structures for rent</td>
                  <td>Plastic Chairs without covers for rent / for hire</td>
                </tr>
                <tr>
                  <td>Mega Hangers for rent</td>
                  <td>AC Tents for rent</td>
                  <td>Round Tables with cloth covering for rent / for hire</td>
                </tr>
                <tr>
                  <td>Wooden Stages for rent</td>
                  <td>Pandals for rent</td>
                  <td>Round Tables without cloth covering for rent / for hire</td>
                </tr>
                <tr>
                  <td>Waterproof Sheds for rent</td>
                  <td>German Hangers for rent / for hire</td>
                  <td>Long Tables with cloth covering for rent / for hire</td>
                </tr>
                <tr>
                  <td>AC German Hangers for rent / for hire</td>
                  <td>VIP Chairs for rent / for hire</td>
                  <td>Long Tables without cloth covering for rent / for hire</td>
                </tr>
                <tr>
                  <td>Air Coolers for rent / for hire</td>
                  <td>Waterproof Pandals for rent / for hire</td>
                  <td>Generators and Generator Sets for rent / for hire</td>
                </tr>
                <tr>
                  <td>Industrial Sheds for rent / for hire</td>
                  <td>Metal barricading for rent / for hire</td>
                  <td>Bronze chain barricading for rent / for hire</td>
                </tr>
                <tr>
                  <td>Banquet Chairs for rent / for hire</td>
                  <td>VVIP Chairs for rent / for hire</td>
                  <td>Synthetic carpets of various colours for rent / for hire</td>
                </tr>
                <tr>
                  <td>Steel Cots for rent / for hire</td>
                  <td>Stages for rent / for hire</td>
                  <td>Two Seater Leather Sofas for rent / for hire</td>
                </tr>
                <tr>
                  <td>Mattresses for rent / for hire</td>
                  <td>Podiums for rent / for hire</td>
                  <td>Maharaja Chairs for rent / for hire</td>
                </tr>
                <tr>
                  <td>Teapoys for rent / for hire</td>
                  <td>Brass Lamps for rent / for hire</td>
                  <td>Stage antique chairs for rent / for hire</td>
                </tr>
                <tr>
                  <td>LED Walls for rent / for hire</td>
                  <td>Flag Poles for rent / for hire</td>
                  <td>LED Lighting for rent / for hire</td>
                </tr>
                <tr>
                  <td>Fans for rent / for hire</td>
                  <td></td>
                  <td></td>
                </tr>
              </tbody>
            </ServicesTable>
          </ServicesSection>
        </Container>
      </RecentWorkSection>

      <GallerySection>
        <Container>
          <GalleryTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Gallery
          </GalleryTitle>

          <FilterContainer className="portfolio-categ filter">
            <FilterItem className={`port-filter all ${activeFilter === 'All' ? 'active' : ''}`}>
              <FilterLink 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  setActiveFilter('All');
                }}
              >
                All
              </FilterLink>
            </FilterItem>
            <FilterItem className={`cat-item-1 ${activeFilter === 'Events' ? 'active' : ''}`}>
              <FilterLink 
                href="#" 
                title="Category 1"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveFilter('Events');
                }}
              >
                Events
              </FilterLink>
            </FilterItem>
            <FilterItem className={`cat-item-2 ${activeFilter === 'Exhibitions' ? 'active' : ''}`}>
              <FilterLink 
                href="#" 
                title="Category 2"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveFilter('Exhibitions');
                }}
              >
                Exhibitions
              </FilterLink>
            </FilterItem>
            <FilterItem className={`cat-item-3 ${activeFilter === 'Weddings' ? 'active' : ''}`}>
              <FilterLink 
                href="#" 
                title="Category 3"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveFilter('Weddings');
                }}
              >
                Weddings
              </FilterLink>
            </FilterItem>
            <FilterItem className={`cat-item-4 ${activeFilter === 'Warehousing' ? 'active' : ''}`}>
              <FilterLink 
                href="#" 
                title="Category 4"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveFilter('Warehousing');
                }}
              >
                Warehousing
              </FilterLink>
            </FilterItem>
            <FilterItem className={`cat-item-5 ${activeFilter === 'German Hangers' ? 'active' : ''}`}>
              <FilterLink 
                href="#" 
                title="Category 5"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveFilter('German Hangers');
                }}
              >
                German Hangers
              </FilterLink>
            </FilterItem>
          </FilterContainer>

          <PortfolioArea className="portfolio-area">
            {filteredGallery.map((item, index) => (
              <PortfolioItem
                key={item.id}
                className="portfolio-item2 mb-2"
                data-id={`id-${index}`}
                data-type={`cat-item-${item.category === 'Events' ? '1' : item.category === 'Exhibitions' ? '2' : item.category === 'Weddings' ? '3' : item.category === 'Warehousing' ? '4' : item.category === 'German Hangers' ? '5' : '1'}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                onClick={() => openLightbox(index)}
              >
                <div>
                  <ImageBlock className="image-block img-hover">
                    <ImageZoom 
                      className="image-zoom" 
                      href={item.image}
                      onClick={(e) => {
                        e.preventDefault();
                        openLightbox(index);
                      }}
                    >
                      <img 
                        src={item.image} 
                        alt={item.alt} 
                        title={item.alt} 
                        className="img-responsive"
                      />
                      <div className="port-info">
                        <h5>{item.category}</h5>
                        <p>Trimurthy Tent House</p>
                      </div>
                    </ImageZoom>
                  </ImageBlock>
                </div>
              </PortfolioItem>
            ))}
          </PortfolioArea>
        </Container>
      </GallerySection>

      <AnimatePresence>
        {lightboxOpen && (
          <Lightbox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <LightboxContent>
              <button className="close-btn" onClick={closeLightbox}>
                <FiX />
              </button>
              <button className="nav-btn prev" onClick={prevImage}>
                <FiChevronLeft />
              </button>
              <button className="nav-btn next" onClick={nextImage}>
                <FiChevronRight />
              </button>
              <img 
                src={filteredGallery[currentImageIndex]?.image} 
                alt={filteredGallery[currentImageIndex]?.alt} 
              />
            </LightboxContent>
          </Lightbox>
        )}
      </AnimatePresence>
    </GalleryContainer>
  );
};

export default Gallery; 